import { CalendarDayIcon, ClockIcon, EllipsisIcon } from "@xola/icons";
import { formatTime, Popover } from "@xola/ui-kit";
import { useHover } from "ahooks";
import { DateFormat, formatDate } from "common";
import { isNil } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { Permission, Product, Timeslot } from "types";
import { useProduct } from "../../hooks/resources/useProduct";
import { getDefaultPhotoUrl } from "../../models/Product";
import { canOpenRoster } from "../../models/Timeslot";
import { IsGranted } from "../Common/IsGranted";
import { Menu } from "../Common/Menu";
import { NoTranslate } from "../Common/NoTranslate";
import { SecondaryButton } from "../Common/SecondaryButton";
import { JoinWaitlistModal } from "../Event/JoinWaitlistModal";
import { EventActionsMenu, EventMenuAction } from "./Common/EventActionsMenu";
import { CreatePurchaseButton } from "./CreatePurchaseButton";

interface DashboardItemActionButtonsProps {
    timeslot: Timeslot;
    size?: string;
}

export const DashboardItemActionButtons = ({ timeslot, size = "small" }: DashboardItemActionButtonsProps) => {
    const { data: product } = useProduct(timeslot.product);
    const [waitlistModal, setWaitlistModal] = useState(false);
    const joinWaitlist = timeslot.canWaitlist;

    const handleClick = (e: any) => {
        e.stopPropagation();
        if (joinWaitlist) {
            setWaitlistModal(true);
        }
    };

    return (
        <div className="ui-dashboard-action-buttons flex items-center space-x-2">
            {canOpenRoster(timeslot) &&
                (product ? (
                    <EventMenuButton size={size} timeslot={timeslot} product={product} />
                ) : (
                    <SecondaryButton disabled size={size} onClick={handleClick}>
                        <EllipsisIcon />
                    </SecondaryButton>
                ))}
            <IsGranted permissions={Permission.PURCHASES_VIEW}>
                <CreatePurchaseButton timeslot={timeslot} />
            </IsGranted>

            {joinWaitlist && (
                <JoinWaitlistModal isOpen={waitlistModal} timeslot={timeslot} onClose={() => setWaitlistModal(false)} />
            )}
        </div>
    );
};

interface EventMenuButtonProps {
    timeslot: Timeslot;
    product: Product;
    size?: string;
}

const EventMenuButton = ({ timeslot, product, size = "small" }: EventMenuButtonProps) => {
    const [isVisible, setIsVisible] = useState(false);
    const [selectedOption, setSelectedOption] = useState<EventMenuAction | null>(null);
    const [hasBeenHovered, setHasBeenHovered] = useState(false);
    const hasSelectedOption = !isNil(selectedOption);

    const ref = useRef(null);
    const isHovering = useHover(ref);

    const handleActionMenuItemClick = (newOption?: EventMenuAction) => {
        if (isNil(newOption)) {
            setSelectedOption(null);
        } else {
            setSelectedOption(newOption);
        }

        setIsVisible(false);
    };

    const handleClickMenu = (e: any) => {
        e.stopPropagation();
        setIsVisible(!isVisible);
    };

    // When the user opens the menu, or hovers the button (we'll take this as an intention to open the menu - perf)
    // Otherwise keep the menu open if the user has already selected an option
    const shouldMountMenu = isVisible || hasSelectedOption || isHovering || hasBeenHovered;

    useEffect(() => {
        if (isHovering && !hasBeenHovered) {
            setHasBeenHovered(true);
        }
    }, [isHovering, hasBeenHovered]);

    return (
        <div className="ui-event-menu-button">
            {/* Using not lazy here, since on the dashboard list/week/day/month it closes everything immediately after selecting an action. */}
            <Popover
                isLazy={false}
                visible={isVisible}
                zIndex={99}
                appendTo={document.body}
                maxWidth={300}
                offset={[0, 10]}
                placement="right"
                onClickOutside={() => setIsVisible(false)}
            >
                <span ref={ref}>
                    <SecondaryButton size={size} onClick={handleClickMenu}>
                        <EllipsisIcon />
                    </SecondaryButton>
                </span>
                <Popover.Content>
                    <Menu>
                        <div className="flex bg-gray-lighter p-5">
                            <img
                                alt={product?.name}
                                className="mr-2 h-10 w-10 rounded object-cover"
                                src={getDefaultPhotoUrl(product)}
                            />

                            <div className="overflow-hidden">
                                <NoTranslate as="div" className="font-bold">
                                    {product?.name}
                                </NoTranslate>

                                <NoTranslate className="text-gray-darker">
                                    <CalendarDayIcon /> {formatDate(timeslot.date, DateFormat.DATE_SHORT)}
                                    {timeslot.time ? (
                                        <>
                                            <ClockIcon className="ml-2" /> {formatTime(timeslot.time)}
                                        </>
                                    ) : null}
                                </NoTranslate>
                            </div>
                        </div>

                        {shouldMountMenu && (
                            // Do not load this component when the popover is not visible, otherwise we will load
                            // a lot of purchase items for this event. Without this flag, the purchase items
                            // for all the events will be loaded when the dashboard loads
                            <EventActionsMenu forceVertical timeslot={timeslot} onClose={handleActionMenuItemClick} />
                        )}
                    </Menu>
                </Popover.Content>
            </Popover>
        </div>
    );
};
